/** Copyright (c) 2017 T. Rowe Price. All rights reserved. */

import './PortfolioGrowth.scss';
import { Spinner } from '@trp-beacon-ui-kit/react-communication';

import { array, bool } from 'prop-types';
import React, {useEffect} from 'react';
import { connect } from 'react-redux';

import DisclosureModal from 'components/shared/Disclosure/DisclosureModal';
import {getAccountGrowth, getCommentary, getConversionHistory} from 'services/Rest';
import Content from 'services/Content';

import PortfolioGrowthCommentary from './PortfolioGrowthCommentary';
import PortfolioGrowthChart from './PortfolioGrowthChart';
import { PORTFOLIO_GROWTH_TYPE } from 'services/Commentary';
import {
  getAccountsForPortfolioGrowth,
  getPortfolioGrowthFilterIsNull,
} from 'state/performance/PortfolioGrowthFilterSelectors';
import {
  getIsRequestingPortfolioGrowthData
} from 'state/requests/RequestStatusSelectors';
import {
  getPortfolioGrowthForGivenRange,
  portfolioGrowthAccountsWithShareClass
} from 'state/performance/PortfolioGrowthSelectors';
import { minInceptionDate } from 'services/Calculation';
import { useDeeplyNestedEffect } from 'support/ReactUtils';
import { isValidPerformanceConstraint } from 'state/performance/PerformancePageUtils';
import PerformanceWarningMessage from 'components/layouts/Performance/PerformanceWarningMessage';
import { getPortfolioAsOfDate } from 'state/portfolio/PortfolioSelectors';
import {yyyymmdd, yyyymmddPortfolioAsOfDate} from 'services/Dates';
import {getConversionHistoryResponse} from 'state/conversionHistory/ConversionHistorySelectors';
import {
  addPortfolioGrowthTableErrorScreenEnabled,
  portfolioGrowthAccountsWithShareClassMessageEnabled
} from 'services/FeatureFlags';
import {getPriorBusinessDay} from 'state/account/AccountSelectors';
import {getAccountDescriptor} from 'services/Accounts';

const callForAccountHistory = (accounts, beginDate, endDate) => {
  if (isValidPerformanceConstraint(accounts, beginDate, endDate)) {
    getAccountGrowth(beginDate, endDate, accounts);
  }
};

const PortfolioGrowth = ({
  accountsFilteredForPerformance,
  isRequesting,
  growth,
  portfolioAsOfDate,
  accountsHaveShareClass,
  conversionHistory,
  priorBusinessDay,
  isPortfolioGrowthNull
}) => {
  useEffect(() => {
    getCommentary(PORTFOLIO_GROWTH_TYPE);
  }, []);

  let accountsEditedForConversionHistory = [];
  if(portfolioGrowthAccountsWithShareClassMessageEnabled() && accountsHaveShareClass.length > 0) {
    accountsEditedForConversionHistory = accountsHaveShareClass.map((account) => {
      return getAccountDescriptor(account)
    });
  }
  useEffect(() => {
    if(portfolioGrowthAccountsWithShareClassMessageEnabled() && accountsHaveShareClass.length > 0) {
      getConversionHistory({
        beginDate: minInceptionDate(accountsHaveShareClass) ? yyyymmdd(minInceptionDate(accountsHaveShareClass)) : yyyymmdd(priorBusinessDay),
        endDate: yyyymmdd(portfolioAsOfDate),
        accounts: accountsEditedForConversionHistory
      });
    }
  }, [accountsHaveShareClass]);

  useEffect(() => {
    const accounts = accountsFilteredForPerformance;
    const endDate = yyyymmddPortfolioAsOfDate(portfolioAsOfDate);
    const beginDate = yyyymmdd(minInceptionDate(accounts));

    callForAccountHistory(accounts, beginDate, endDate);
  }, [portfolioAsOfDate]);

  useDeeplyNestedEffect(accountsFilteredForPerformance, () => {
    const accounts = accountsFilteredForPerformance;
    const endDate = yyyymmddPortfolioAsOfDate(portfolioAsOfDate);
    const beginDate = yyyymmdd(minInceptionDate(accounts));

    callForAccountHistory(accounts, beginDate, endDate);
  });

  const showChart = accountsFilteredForPerformance.length > 0 && growth;

  const ChartDisplay = () => {
    if (isRequesting) {
      return <Spinner />;
    }
    return !isPortfolioGrowthNull && showChart ? (
      <PortfolioGrowthChart growth={growth} />
    ) : addPortfolioGrowthTableErrorScreenEnabled() && isPortfolioGrowthNull ? (
      <PerformanceWarningMessage
        message={Content.portfolioGrowthChartWarningCallFailed}
      />
      ) : (
      <PerformanceWarningMessage
        message={Content.portfolioGrowthChartWarningMessage}
      />
    );
  };

  return (
    <div className="portfolio-growth module module-container">
      <div className="pure-u-3-4 pure-u-sm-1">
        <ChartDisplay />
      </div>
      <div className="pure-u-1-4 pure-u-sm-1 commentary-sidebar">
        {portfolioGrowthAccountsWithShareClassMessageEnabled() ? (
        <PortfolioGrowthCommentary
          conversionHistory={conversionHistory}
        />
        ) : (
          <PortfolioGrowthCommentary />
        )}
        <div className="disclosure">
          <p className="statement">
            Past performance cannot guarantee future results.
          </p>
          <div className="see-table growth">
            To see the accounts included in the analysis, refer to the “View
            Activity Summary Breakdown” table above.
          </div>
          <div className="purged-account-copy growth">
            Closed accounts are not included.
          </div>
          <DisclosureModal
            className="growth"
            iconAfter={true}
            content={Content.portfolioGrowthDisclosure}
          />
        </div>
      </div>
    </div>
  );
};

PortfolioGrowth.propTypes = {
  accountsFilteredForPerformance: array.isRequired,
  isRequesting: bool,
  growth: array
};

const mapStateToProps = (state) => {
  return {
    accountsFilteredForPerformance: getAccountsForPortfolioGrowth(state),
    isRequesting: getIsRequestingPortfolioGrowthData(state),
    growth: getPortfolioGrowthForGivenRange(state),
    isPortfolioGrowthNull: getPortfolioGrowthFilterIsNull(state),
    portfolioAsOfDate: getPortfolioAsOfDate(state),
    accountsHaveShareClass: portfolioGrowthAccountsWithShareClass(state),
    conversionHistory: getConversionHistoryResponse(state),
    priorBusinessDay: getPriorBusinessDay(state),
  };
};

export default connect(mapStateToProps)(PortfolioGrowth);
